/* eslint-disable no-return-assign */
import '@fontsource/poppins';
import axios from 'axios';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

import React, { useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { DeleteButton, EditButton } from '@src/components/buttons';
import Container from '@src/components/commons/containerPage.common';
import { useAuth } from '@src/contexts/authContext';
import { formatDate } from '@src/utils';

const url = `${process.env.REACT_APP_API_URL}`;

export default function CampaignTemplate() {
  const { auth } = useAuth();
  const path = useLocation().pathname;
  const navigate = useNavigate();
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState();
  const [deleteStatus, setDeleteStatus] = useState();
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/v3/messages/twilio/templates/${id}`,
        {
          headers: {
            Authorization: `Bearer ${auth.idToken}`,
            'My-Custom-Header': 'foobar',
          },
        },
      );

      if (response.status === 200) {
        console.log(response);
        setDeleteStatus('Delete successful');
        setShowDeleteSuccess(true);
      } else {
        console.error('Delete operation failed');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const showDeleteConfirmationDialog = (id) => {
    setDeleteId(id);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmationDialogClose = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmationDialogConfirm = () => {
    handleDelete(deleteId);
    setShowDeleteConfirmation(false);
  };

  const [searchName, setSearchName] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchApproval, setSearchApproval] = useState('');
  const [searchCategory, setSearchCategory] = useState('');

  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const fetchData = (pages = 0, limit = 10) => {
    setLoading(true);

    if (!auth.idToken) return;

    const params = new URLSearchParams();

    params.append('page', pages + 1);
    params.append('limit', limit);

    if (searchName) params.append('search', searchName);
    if (searchCategory) params.append('category', searchCategory);
    if (searchApproval) params.append('approvalStatus', searchApproval);
    if (searchStatus !== '') {
      params.append('isActive', searchStatus);
    }

    axios
      .get(`${url}/admin/v3/messages/twilio/templates?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${auth.idToken}`,
        },
      })
      .then((response) => {
        const res = response.data?.response || {};
        setTemplate(res.data || []);
        setPagination(res.pagination || {});
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useEffect(() => {
    fetchData(0, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.idToken,
    searchName,
    searchApproval,
    searchStatus,
    searchCategory,
    deleteStatus,
  ]);

  const handleUpdateCampaign = (id) => {
    navigate(`/update-campaign-template/${id}`);
  };

  return (
    <Container pathUrl={path}>
      <div className='px-4 pt-8'>
        <div className='flex justify-start pr-10'>
          <Box
            sx={{ width: '100%', height: 'auto' }}
            className='rounded-xl bg-white px-6 py-4'
          >
            <Grid container spacing={3}>
              {/* Title */}
              <Grid item xs={12} sm={6} md={2.5}>
                <Typography className='font-poppins text-sm text-gray-400'>
                  Name
                </Typography>
                <TextField
                  fullWidth
                  size='medium'
                  variant='outlined'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'white',
                      height: '46px',
                    },
                    mt: 1,
                  }}
                  value={searchName || ''}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder='Search by name'
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <Typography className='font-poppins text-sm text-gray-400'>
                  Category
                </Typography>
                <Select
                  fullWidth
                  displayEmpty
                  sx={{
                    height: '46px',
                    backgroundColor: 'white',
                    mt: 1,
                    borderRadius: 2,
                  }}
                  MenuProps={{
                    disablePortal: true,
                    PaperProps: { style: { zIndex: 1302 } },
                  }}
                  value={searchCategory || ''}
                  onChange={(e) => setSearchCategory(e.target.value)}
                >
                  <MenuItem disabled value=''>
                    <em style={{ color: '#8C8C8C' }}>Select</em>
                  </MenuItem>
                  <MenuItem value=''>ALL</MenuItem>
                  <MenuItem value='MARKETING'>Marketing</MenuItem>
                  <MenuItem value='UTILITY'>Utility</MenuItem>
                  <MenuItem value='UNCATEGORIZED'> Uncategorized</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <Typography className='font-poppins text-sm text-gray-400'>
                  Approval Status
                </Typography>
                <Select
                  fullWidth
                  displayEmpty
                  sx={{
                    height: '46px',
                    backgroundColor: 'white',
                    mt: 1,
                    borderRadius: 2,
                  }}
                  MenuProps={{
                    disablePortal: true,
                    PaperProps: { style: { zIndex: 1302 } },
                  }}
                  value={searchApproval || ''}
                  onChange={(e) => setSearchApproval(e.target.value)}
                >
                  <MenuItem disabled value=''>
                    <em style={{ color: '#8C8C8C' }}>Select</em>
                  </MenuItem>
                  <MenuItem value=''>ALL</MenuItem>
                  <MenuItem value='approved'>Approved</MenuItem>
                  <MenuItem value='rejected'>Rejected</MenuItem>
                  <MenuItem value='unsubmitted'>Unsubmitted</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <Typography className='font-poppins text-sm text-gray-400'>
                  Is Active
                </Typography>
                <Select
                  fullWidth
                  displayEmpty
                  sx={{
                    height: '46px',
                    backgroundColor: 'white',
                    mt: 1,
                    borderRadius: 2,
                  }}
                  MenuProps={{
                    disablePortal: true,
                    PaperProps: { style: { zIndex: 1302 } },
                  }}
                  // value={searchStatus || ''}
                  value={
                    searchStatus === true
                      ? 'true'
                      : searchStatus === false
                      ? 'false'
                      : ''
                  }
                  onChange={(e) => setSearchStatus(e.target.value)}
                >
                  <MenuItem disabled value=''>
                    <em style={{ color: '#8C8C8C' }}>Select</em>
                  </MenuItem>
                  <MenuItem value=''>ALL</MenuItem>
                  <MenuItem value='true'>True</MenuItem>
                  <MenuItem value='false'>False</MenuItem>
                </Select>
              </Grid>
              &nbsp;
              <Button
                variant='contained'
                className='mt-14 ml-3 rounded-md bg-cyan-600 py-2 text-sm font-bold'
                sx={{
                  width: '80px',
                  height: '38px',
                  textTransform: 'none',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                }}
                onClick={() => {
                  setSearchName('');
                  setSearchStatus('');
                  setSearchCategory(null);
                  setSearchApproval(null);
                  fetchData();
                }}
              >
                Clear
              </Button>
            </Grid>
          </Box>
        </div>
        {loading ? (
          <div className='font-poppins py-6 px-8 text-gray-500'>Loading...</div>
        ) : (
          <span className='flex flex-col items-start pr-10 pt-8'>
            <TableContainer
              className='rounded-xl'
              component={Paper}
              style={{
                width: '100%',
                paddingBottom: '12px',
                paddingRight: '12px',
                backgroundColor: '#ffffff',
                borderColor: 'ffffff',
              }}
            >
              <Table
                sx={{
                  minWidth: 350,
                  backgroundColor: '#ffffff',
                  borderColor: 'ffffff',
                }}
                aria-label='simple table'
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{
                        width: '400px',
                        border: 'gray',
                        paddingLeft: 20,
                      }}
                    >
                      Name / Template SID
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '100px', border: 'gray' }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '80px', border: 'gray' }}
                    >
                      Language
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '100px', border: 'gray' }}
                    >
                      Approval Status
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '80px', border: 'gray' }}
                    >
                      Is Active
                    </TableCell>
                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '200px', border: 'gray' }}
                    >
                      Updated At
                    </TableCell>

                    <TableCell
                      className='text-xs font-bold text-gray-400'
                      style={{ padding: 4, width: '200px', border: 'gray' }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(template) && template.length > 0 ? (
                    template.map((row) => (
                      <TableRow
                        key={row?._id}
                        hover
                        tabIndex={-1}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                          },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <TableCell
                          sx={{ pl: 3, py: 1.5 }}
                          className='text-sm font-bold text-gray-500'
                        >
                          {row?.ct_name || '-'}
                        </TableCell>

                        <TableCell
                          sx={{ py: 1.5 }}
                          className='text-sm font-bold text-gray-500'
                        >
                          {row.ct_category}
                        </TableCell>

                        <TableCell
                          sx={{ py: 1.5 }}
                          className='text-sm font-bold text-gray-500'
                        >
                          {row.ct_language}
                        </TableCell>

                        <TableCell
                          sx={{ py: 1.5 }}
                          className='text-sm font-bold text-gray-500'
                        >
                          <Box
                            sx={{
                              backgroundColor:
                                row?.ct_approval_status === 'approved'
                                  ? '#DCFCE7'
                                  : row?.ct_approval_status === 'unsubmitted'
                                  ? '#E0F2FE'
                                  : '#FEF3C7',
                              color:
                                row?.ct_approval_status === 'approved'
                                  ? '#15803D'
                                  : row?.ct_approval_status === 'unsubmitted'
                                  ? '#0369A1'
                                  : '#92400E',
                              borderRadius: '12px',
                              px: 1.5,
                              py: 0.5,
                              display: 'inline-block',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'capitalize',
                            }}
                          >
                            {row?.ct_approval_status}
                          </Box>
                        </TableCell>

                        <TableCell
                          sx={{ py: 1.5 }}
                          className='text-sm font-bold text-gray-500'
                        >
                          {row?.ct_is_active ? 'true' : 'false'}
                        </TableCell>

                        <TableCell
                          sx={{ py: 1.5 }}
                          className='text-sm font-bold text-gray-500'
                        >
                          {formatDate(row?.ww_updated_at)}
                        </TableCell>

                        <TableCell sx={{ py: 1.5 }}>
                          <Box display='flex' alignItems='center' gap={1.2}>
                            <EditButton
                              onClick={() => handleUpdateCampaign(row?.the_id)}
                            />

                            <DeleteButton
                              onClick={() =>
                                showDeleteConfirmationDialog(row?.the_id)
                              }
                            />
                          </Box>

                          {showDeleteConfirmation && (
                            <Dialog
                              open={showDeleteConfirmation}
                              onClose={handleConfirmationDialogClose}
                            >
                              <DialogTitle sx={{ fontWeight: 600 }}>
                                Confirm Delete
                              </DialogTitle>
                              <DialogContent>
                                <DialogContentText>
                                  Are you sure you want to delete this template?
                                </DialogContentText>
                              </DialogContent>
                              <DialogActions>
                                <Button
                                  onClick={handleConfirmationDialogClose}
                                  color='primary'
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleConfirmationDialogConfirm}
                                  color='error'
                                >
                                  Delete
                                </Button>
                              </DialogActions>
                            </Dialog>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align='center'>
                        <Typography className='font-poppins py-6 italic text-gray-400'>
                          No template listed
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Box
                className='flex justify-end py-3'
                sx={{ width: '100%', fontFamily: 'Poppins' }}
              >
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component='div'
                  count={pagination.totalItems}
                  rowsPerPage={pagination.itemsPerPage}
                  page={pagination.currentPage - 1}
                  onPageChange={(e, newPage) =>
                    fetchData(newPage, pagination.itemsPerPage)
                  }
                  onRowsPerPageChange={(e) => {
                    const newRows = parseInt(e.target.value, 10);
                    fetchData(0, newRows);
                  }}
                />
              </Box>
            </TableContainer>
          </span>
        )}
      </div>
      <br />
      <br />
      <br />

      {showDeleteSuccess && (
        <Dialog
          open={showDeleteSuccess}
          onClose={() => setShowDeleteSuccess(false)}
        >
          <DialogTitle>Delete Successful</DialogTitle>
          <DialogContent>
            <DialogContentText>
              The template has been deleted successfully.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteSuccess(false)} color='primary'>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
}
